import { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import NotificationCenter from './NotificationCenter';
import AdminPresenceIndicator from './AdminPresenceIndicator';
import { useModerationCounts } from '@/hooks/useRealtime';

export default function Header() {
  const { setSidebarOpen } = useUIStore();
  const { user, role, signOut } = useAuthStore();
  const navigate = useNavigate();

  // Get real-time moderation counts for sidebar badge
  const { counts } = useModerationCounts();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
      <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
        {/* Mobile menu button */}
        <button
          type="button"
          className="lg:hidden p-2 text-gray-500 hover:text-gray-600"
          onClick={() => setSidebarOpen(true)}
        >
          <Bars3Icon className="w-6 h-6" />
        </button>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right side */}
        <div className="flex items-center gap-4">
          {/* Admin presence - hidden on mobile */}
          <AdminPresenceIndicator className="hidden md:flex" />

          {/* Real-time Notification Center */}
          <NotificationCenter />

          {/* Moderation badge showing pending counts */}
          {counts.total > 0 && (
            <button
              type="button"
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-100 rounded-full hover:bg-orange-200"
              onClick={() => navigate('/moderation')}
            >
              <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
              {counts.total} pending
            </button>
          )}

          {/* User menu */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center gap-2 p-2 text-gray-700 hover:text-gray-900">
              <UserCircleIcon className="w-8 h-8 text-gray-400" />
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium">{user?.email}</p>
                <p className="text-xs text-gray-500 capitalize">{role?.replace('_', ' ')}</p>
              </div>
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="p-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => navigate('/settings')}
                        className={`${
                          active ? 'bg-gray-100' : ''
                        } flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 rounded-md`}
                      >
                        <Cog6ToothIcon className="w-5 h-5" />
                        Settings
                      </button>
                    )}
                  </Menu.Item>
                  <div className="my-1 border-t border-gray-100" />
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleSignOut}
                        className={`${
                          active ? 'bg-gray-100' : ''
                        } flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 rounded-md`}
                      >
                        <ArrowRightOnRectangleIcon className="w-5 h-5" />
                        Sign out
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </header>
  );
}
